/**
 * UnlockBeneficiosModal: modal para que un usuario público o no-al-día
 * verifique su matrícula y "desbloquee" el acceso a los CTAs de los beneficios.
 *
 * Reutilizado desde la home (`/beneficios`) y el detalle de prestador
 * cuando alguien intenta usar un CTA locked.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v) => void
 *  - onUnlocked: (usuario) => void  // Se llama cuando la verificación tiene éxito
 *  - motivo: string opcional para contextualizar (ej: "generar un voucher")
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Loader2, Lock, KeyRound, HelpCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UnlockBeneficiosModal({
  open,
  onOpenChange,
  onUnlocked,
  motivo = 'usar este beneficio',
}) {
  const [matricula, setMatricula] = useState('');
  const [documento, setDocumento] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setMatricula('');
    setDocumento('');
    setError(null);
    setLoading(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleVerificar = async (e) => {
    e.preventDefault();
    if (!matricula.trim() || !documento.trim()) {
      setError('Completá matrícula y documento.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/beneficios/verificar-acceso-antuar`, {
        matricula: matricula.trim(),
        documento: documento.trim(),
      });
      const trabajador = response.data.trabajador || {};
      if (response.data.puede_acceder) {
        const usuario = {
          ...trabajador,
          matricula: matricula.trim(),
          documento: documento.trim(),
          al_dia: true,
        };
        sessionStorage.setItem('beneficios_usuario', JSON.stringify(usuario));
        if (onUnlocked) onUnlocked(usuario);
        handleClose(false);
      } else {
        const usuario = {
          ...trabajador,
          matricula: matricula.trim(),
          documento: documento.trim(),
          al_dia: false,
          mensaje_deuda: response.data.mensaje,
          deuda_info: response.data.deuda_info,
        };
        sessionStorage.setItem('beneficios_usuario', JSON.stringify(usuario));
        if (onUnlocked) onUnlocked(usuario);
        handleClose(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo verificar. Intentá de nuevo o comunicate con el Colegio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="unlock-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <span>Verificá tu matrícula</span>
          </DialogTitle>
          <DialogDescription>
            Ingresá tu matrícula y DNI para desbloquear los beneficios de la guía. Son exclusivos para matriculados al día del CATSLZ.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-gray-600 leading-relaxed">
          Para {motivo}, ingresá tu <strong>matrícula</strong> y <strong>DNI</strong>. Los beneficios son
          exclusivos para matriculados al día del CATSLZ.
        </p>

        <form onSubmit={handleVerificar} className="space-y-4">
          <div>
            <Label htmlFor="unlock-matricula">Matrícula</Label>
            <Input
              id="unlock-matricula"
              type="text"
              value={matricula}
              onChange={(e) => setMatricula(e.target.value)}
              placeholder="Ej: 1234"
              autoFocus
              data-testid="unlock-matricula-input"
            />
          </div>
          <div>
            <Label htmlFor="unlock-documento">Documento (DNI)</Label>
            <Input
              id="unlock-documento"
              type="text"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              placeholder="Sin puntos ni espacios"
              data-testid="unlock-documento-input"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={loading}
            className="w-full bg-gray-900 hover:bg-black text-white font-semibold rounded-xl"
            data-testid="unlock-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verificando…
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-2" />
                Verificar matrícula
              </>
            )}
          </Button>
        </form>

        <div className="pt-3 border-t border-gray-100 space-y-2">
          <p className="text-xs text-gray-500 text-center">
            ¿No sabés si estás al día o querés regularizar tu situación?
          </p>
          <div className="flex gap-2">
            <a
              href="https://servicios.colegiotslomas.org/estado-cuenta"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="unlock-consulta-deuda-link"
            >
              <Button variant="outline" className="w-full text-xs" type="button">
                <HelpCircle className="w-3 h-3 mr-1" />
                Consultar mi deuda
              </Button>
            </a>
            <a
              href="mailto:info@colegiotslomas.org"
              className="flex-1"
              data-testid="unlock-mail-administracion"
            >
              <Button variant="outline" className="w-full text-xs" type="button">
                Contactar administración
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
